import {
  faCalculator,
  faCalendar,
  faLockKeyhole,
  faLockKeyholeOpen,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  FormHelperText,
  TextField as MuiTextField,
  Tooltip,
} from '@mui/material';
import * as Ramda from 'ramda';
import React from 'react';
import { Modal } from '../../../../../../../../components/modals/v2/helpers/v2.modal.helpers';
import { Show } from '../../../../../../../../components/Show';
import { ProductTypes } from '../../../../../../../../types';
import TickedSlider from '../../../../../../../agencies/quotes/PrimeX/customizeQuote/components/TickedSlider';
import SelectBase from '../../../../../../primeX-underwriter/components/footer/SelectBase';
import { useLocale } from '../../../../providers/LocaleProvider';
import { isUnderwriteQuoteState } from '../../../customize-quote/types';
import useTypingTimeout from '../../hooks/useTypingTimeout';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';
import { isCoverageEndorsementsState } from '../../types';
import CoverageButton from '../coverages/amendatory-coverages/shared/CoverageButton';
import { ReverseIrpmModalConfig } from '../coverages/modals/ReverseIrpmModal';
import type ModifierInputFactory from './ModifierInputFactory';
import type { RateModifier } from '../../../../api/types/rater-x-preset';
import { isScheduleRating, ModifierTypes } from './modifierTypes';
import { showsReverseIrpmCalculator } from './reverseIrpm';
import { ScheduleRatingModalConfig } from '../rater-x-components/modals/ScheduleRating';

export function DropDown(props: Props) {
  const _options = props.rules?.factor.options?.map((opt: string) => ({
    label: `${opt}`,
    value: opt,
  }));

  return (
    <SelectBase
      options={_options ?? []}
      name={props.id}
      value={props.value.props.factor?.value}
      onChange={(e) => {
        props.handleChange({
          value: { props: { factor: { value: e.target.value as number } } },
        });
      }}
      sx={{
        '& .MuiSelect-select': {
          minHeight: '0.2rem',
          height: '0.50rem',
          width: '8rem',
        },
      }}
    />
  );
}

export function Slider(props: Props) {
  const accessPaths =
    modifierAccessPaths[props.type as keyof typeof modifierAccessPaths];
  const min = Number(Ramda.path(accessPaths.min, props));
  const max = Number(Ramda.path(accessPaths.max, props));
  const step = Number(Ramda.path(accessPaths.step, props));
  const defaultValue = Number(Ramda.path(accessPaths.defaultValue, props));

  const handleChange = useTypingTimeout((newValue: number | number[]) => {
    props.handleChange({
      value: accessPaths.dispatch(String(newValue)),
    });
  });

  return (
    <TickedSlider
      boxProps={{
        alignItems: 'center',
        gap: '1rem',
        width: '14rem',
        marginLeft: '1.5rem',
      }}
      onChange={handleChange.onChange}
      min={min}
      initialSliderValue={defaultValue}
      max={max}
      step={step}
      disabled={min === max}
    >
      {(value) => <>{value}</>}
    </TickedSlider>
  );
}

export function TextField(props: Props) {
  const context = useUnderwriterContext();
  const accessPaths =
    modifierAccessPaths[props.type as keyof typeof modifierAccessPaths];

  const min = Number(Ramda.path(accessPaths.min, props));
  const max = Number(Ramda.path(accessPaths.max, props));
  const step = Number(Ramda.path(accessPaths.step, props));

  const isLocked =
    (context.state.productType === ProductTypes.prime_tech &&
      props.id === ModifierTypes.ADMIN_FEE &&
      isCoverageEndorsementsState(context.state)) ||
    props.id === ModifierTypes.RENEWAL_RATING_FACTOR;

  const validateInput = (value: number) => {
    if (!Ramda.isNil(min) && !Ramda.isNil(max)) {
      if (value < min || value > max) {
        return 'Invalid';
      }
    }

    // FIXME: This is temporary, Rater should validate this.
    if (
      String(value).split('.')?.[1]?.length > 1 &&
      props.id === ModifierTypes.COMMISSION
    ) {
      return 'Invalid';
    }
  };

  const typingTimeout = useTypingTimeout(
    (value) => {
      props.handleChange({
        value: accessPaths.dispatch(value),
      });
    },
    {
      validate: validateInput,
      defaultValue: Ramda.path(accessPaths.defaultValue, props),
    }
  );

  return (
    <Box display="flex" alignItems="center">
      <FormHelperText style={{ marginTop: 0, marginRight: '0.5rem' }}>
        {typingTimeout.error}
      </FormHelperText>
      <InputHelperVariants {...props} />

      <MuiTextField
        size="small"
        name={props.type}
        type="number"
        disabled={min === max || isLocked}
        value={typingTimeout.input}
        onChange={(e) => typingTimeout.onChange(e.target.value)}
        inputProps={{
          step,
          min,
          max,
        }}
        sx={{
          '& .MuiInputBase-input': {
            height: '0.75rem',
            width: '8rem',
            textAlign: 'left',
          },
        }}
      />
    </Box>
  );
}

export function CommissionInput(props: Props) {
  const accessPaths =
    modifierAccessPaths[props.type as keyof typeof modifierAccessPaths];

  const min = Number(Ramda.path(accessPaths.min, props));
  const max = Number(Ramda.path(accessPaths.max, props));
  const step = Number(Ramda.path(accessPaths.step, props));

  const validateInput = (value: number) => {
    if (!Ramda.isNil(min) && !Ramda.isNil(max)) {
      if (value < min || value > max) {
        return 'Invalid';
      }
    }

    // FIXME: This is temporary, Rater should validate this.
    if (String(value).split('.')?.[1]?.length > 1) {
      return 'Invalid';
    }
  };

  const typingTimeout = useTypingTimeout(
    (value) => {
      props.handleChange({
        value: accessPaths.dispatch(value),
      });
    },
    {
      validate: validateInput,
      defaultValue: Ramda.path(accessPaths.defaultValue, props),
    }
  );

  return (
    <Box display="flex" alignItems="center" justifyContent="end">
      <FormHelperText style={{ marginTop: 0, marginRight: '0.5rem' }}>
        {typingTimeout.error}
      </FormHelperText>
      <InputHelperVariants {...props} />

      <MuiTextField
        size="small"
        name={props.type}
        type="number"
        disabled={min === max || props.rules == null || props.disabled}
        value={typingTimeout.input}
        onChange={(e) => typingTimeout.onChange(e.target.value)}
        inputProps={{
          step,
          min,
          max,
        }}
        sx={{
          '& .MuiInputBase-input': {
            height: '0.75rem',
            width: '8rem',
            textAlign: 'left',
          },
        }}
      />
    </Box>
  );
}

export const ScheduleRatingInput = (props: Props) => {
  return (
    <Box display="flex" alignItems="center" gap="0.5rem">
      <IrpmField {...props} />
      <Tooltip title="Scheduled Rating justifications">
        <CoverageButton
          onClick={Modal.deferShow(ScheduleRatingModalConfig, {
            data: {
              ratingOptionsState: props,
              onSubmit: props.handleChange,
            },
          })}
          sx={{ p: '0.5rem', width: '3rem', minWidth: '3rem' }}
        >
          <FontAwesomeIcon icon={faCalculator} />
        </CoverageButton>
      </Tooltip>
    </Box>
  );
};

// Unlocking reverse IRPM clears the target premium. The modifier reducer overlays
// the incoming value.props onto the existing props, so the target must be sent as
// an explicit `undefined` to drop it from state (and from the rater payload).
export function buildReverseIrpmUnlockChange(
  value: RateModifier['value']
): Pick<RateModifier, 'value'> {
  return {
    value: {
      props: {
        factor: value.props.factor,
        targetTotalPremium: undefined,
      },
    },
  };
}

export function IrpmField(props: Props) {
  const { state: quoteState } = useUnderwriterContext();
  const { language } = useLocale();
  const isReverseIrpmLocked = Boolean(props.value.props.targetTotalPremium);
  const accessPaths =
    modifierAccessPaths[props.type as keyof typeof modifierAccessPaths];

  const min = Number(Ramda.path(accessPaths.min, props));
  const max = Number(Ramda.path(accessPaths.max, props));
  const step = Number(Ramda.path(accessPaths.step, props));

  // P100 Pro's UW factors are read-only here (its IRPM was retired), except Schedule
  // Rating and the PS-8363 Correction Factor, which exist to be typed into.
  const isP100ProReadOnlyFactor =
    quoteState.productType === ProductTypes.p100_pro &&
    !isScheduleRating(props.id) &&
    props.id !== ModifierTypes.CORRECTION_FACTOR;

  const validateInput = (value: number) => {
    if (!Ramda.isNil(min) && !Ramda.isNil(max)) {
      if (value < min || value > max) {
        return 'Invalid';
      }
    }
  };

  const typingTimeout = useTypingTimeout(
    (value) => {
      props.handleChange({
        value: accessPaths.dispatch(value),
      });
    },
    {
      validate: validateInput,
      defaultValue: Ramda.path(accessPaths.defaultValue, props),
    }
  );

  let linkProps = {
    icon: faLockKeyholeOpen,
    tooltip:
      'Reverse IRPM is not locked in. Please enter a target premium by clicking on the calculator icon.',
  };

  if (isReverseIrpmLocked) {
    linkProps = {
      icon: faLockKeyhole,
      tooltip:
        'Reverse IRPM is locked in. As you make changes IRPM will automatically adjust to maintain the same total premium. Click to unlock.',
    };
  }

  return (
    <Box display="flex" alignItems="center" gap="0.5rem">
      <FormHelperText style={{ marginTop: 0, marginRight: '0.5rem' }}>
        {typingTimeout.error}
      </FormHelperText>
      <Show when={!Ramda.isNil(min) && !Ramda.isNil(max)}>
        <i>
          (Min: {min}, Max: {max})
        </i>
      </Show>

      <MuiTextField
        size="small"
        name={props.type}
        type="number"
        disabled={min === max || isP100ProReadOnlyFactor}
        value={typingTimeout.input}
        onChange={(e) => typingTimeout.onChange(e.target.value)}
        inputProps={{
          step,
          min,
          max,
        }}
        sx={{
          '& .MuiInputBase-input': {
            height: '0.75rem',
            width: '8rem',
            textAlign: 'left',
          },
        }}
      />

      <Show
        when={
          showsReverseIrpmCalculator(quoteState.productType, props.id) &&
          isUnderwriteQuoteState(quoteState)
        }
      >
        <CoverageButton
          onClick={Modal.deferShow(ReverseIrpmModalConfig, {
            data: {
              ...props,
              language,
              totalPremium: quoteState.totalPremium,
            },
          })}
        >
          <FontAwesomeIcon style={{ fontSize: '1.5rem' }} icon={faCalculator} />
        </CoverageButton>

        <Tooltip title={linkProps.tooltip}>
          <Box>
            <CoverageButton
              disabled={!isReverseIrpmLocked}
              onClick={() => {
                props.handleChange(buildReverseIrpmUnlockChange(props.value));
              }}
            >
              <FontAwesomeIcon
                style={{ fontSize: '1.5rem' }}
                icon={linkProps.icon}
              />
            </CoverageButton>
          </Box>
        </Tooltip>
      </Show>
    </Box>
  );
}

type Props = React.ComponentProps<typeof ModifierInputFactory>;

export const modifierAccessPaths = {
  FEE: {
    min: ['rules', 'fee', 'from'],
    max: ['rules', 'fee', 'to'],
    step: ['rules', 'fee', 'step'],
    defaultValue: ['value', 'props', 'fee', 'value'],
    dispatch: (value: string) => ({ props: { fee: { value: Number(value) } } }),
  },
  COMMISSION_RATE: {
    min: ['rules', 'rate', 'from'],
    max: ['rules', 'rate', 'to'],
    step: ['rules', 'rate', 'step'],
    defaultValue: ['value', 'props', 'rate', 'value'],
    dispatch: (value: string) => ({
      props: { rate: { value: Number(value) } },
    }),
  },
  UW_MODIFIER: {
    min: ['rules', 'factor', 'from'],
    max: ['rules', 'factor', 'to'],
    step: ['rules', 'factor', 'step'],
    defaultValue: ['value', 'props', 'factor', 'value'],
    dispatch: (value: string) => ({
      props: { factor: { value: Number(value) } },
    }),
  },
};

function InputHelperVariants(props: Props) {
  let label = null;
  if (props.id === ModifierTypes.ADMIN_FEE) {
    if (props?.value?.props?.isAdminFeeTaxed === false) {
      label = 'Included in premium';
    } else if (props?.value?.props?.isAdminFeeTaxed) {
      label = 'Not included in premium';
    }
  }

  if (!label) {
    return null;
  }

  return <i>{label}</i>;
}
